// Demo mode in-memory data store
// Resets every page refresh — session-only, no persistence
import type {
  User, Organization, Branch, Camera, CameraWithKey, CameraSettingsHistory,
  Face, Zone, ZoneAnalyticsSummary, ZoneCountEvent, LatestZoneCount,
  AnalyticsSummary, TimeseriesPoint, EngagementMetrics, TrafficPatterns,
  CrossDemographicInsights, VideoStatsEntry, LiveFeedEvent,
  Product, Video, MediaPlayer, MediaPlayerWithKey, PlaylistItem, Alert, SnapshotStatus, ZoneVideoMapping,
} from '../types';
import type { LiveCameraStats } from './api';

// ─── Helpers ──────────────────────────────────────────────────
const uuid = () => crypto.randomUUID();
const now = () => new Date().toISOString();
const ago = (ms: number) => new Date(Date.now() - ms).toISOString();
const rand = (min: number, max: number) => Math.floor(Math.random() * (max - min + 1)) + min;
const pick = <T>(arr: T[]): T => arr[Math.floor(Math.random() * arr.length)];

const AGE_GROUPS = ['0-10', '11-17', '18-24', '25-34', '35-44', '45-54', '55-64', '65+'] as const;
const EXPRESSIONS = ['neutral', 'happy', 'sad', 'angry', 'fearful', 'disgusted', 'surprised'] as const;
const GENDERS = ['male', 'female'] as const;

// ─── IDs (stable across session) ──────────────────────────────
const ORG_ID = 'demo-org';
const BRANCH_IDS = ['branch-1', 'branch-2', 'branch-3'];
const CAMERA_IDS = ['cam-1', 'cam-2', 'cam-3', 'cam-4', 'cam-5', 'cam-6', 'cam-7', 'cam-8', 'cam-9', 'cam-10'];
const FACE_IDS = ['face-1', 'face-2', 'face-3', 'face-4'];
const ZONE_IDS = ['zone-1', 'zone-2', 'zone-3', 'zone-4', 'zone-5', 'zone-6'];
const VIDEO_IDS = ['vid-1', 'vid-2', 'vid-3'];
const PLAYER_IDS = ['player-1', 'player-2'];

// ─── Color SVG thumbnail generator ───────────────────────────
function svgThumbnail(label: string, hue: number): string {
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="120" height="120">
    <rect width="120" height="120" rx="12" fill="hsl(${hue},60%,35%)"/>
    <text x="60" y="65" text-anchor="middle" fill="white" font-size="14" font-family="sans-serif">${label}</text>
  </svg>`;
  return `data:image/svg+xml;base64,${btoa(svg)}`;
}

// ─── Base Data Factory ────────────────────────────────────────
function createBaseData() {
  const branches: Branch[] = [
    { id: BRANCH_IDS[0], organization_id: ORG_ID, name: 'Mall of Africa', address: 'Midrand, Gauteng', created_at: ago(90 * 86400000), updated_at: ago(30 * 86400000) },
    { id: BRANCH_IDS[1], organization_id: ORG_ID, name: 'V&A Waterfront', address: 'Cape Town, Western Cape', created_at: ago(60 * 86400000), updated_at: ago(14 * 86400000) },
    { id: BRANCH_IDS[2], organization_id: ORG_ID, name: 'Sandton City', address: 'Sandton, Gauteng', created_at: ago(45 * 86400000), updated_at: ago(7 * 86400000) },
  ];

  const cameras: Camera[] = [
    { id: CAMERA_IDS[0], organization_id: ORG_ID, branch_id: BRANCH_IDS[0], name: 'Main Entrance', location: 'Ground floor entrance', is_active: true, is_online: true, last_seen_at: ago(5000), created_at: ago(80 * 86400000), face_retention_minutes: 30, face_match_threshold: 0.6, detection_mode: 'both', camera_type: 'client', detection_confidence: 0.35, detection_input_size: 608, tracking_spatial_weight: 0.4, tracking_snap_threshold: 1.5 },
    { id: CAMERA_IDS[1], organization_id: ORG_ID, branch_id: BRANCH_IDS[0], name: 'Aisle 3 - Electronics', location: 'First floor', is_active: true, is_online: true, last_seen_at: ago(3000), created_at: ago(75 * 86400000), face_retention_minutes: 30, face_match_threshold: 0.6, detection_mode: 'both', camera_type: 'client', detection_confidence: 0.35, detection_input_size: 608, tracking_spatial_weight: 0.4, tracking_snap_threshold: 1.5 },
    { id: CAMERA_IDS[2], organization_id: ORG_ID, branch_id: BRANCH_IDS[1], name: 'Food Court', location: 'Level 2', is_active: true, is_online: true, last_seen_at: ago(2000), created_at: ago(55 * 86400000), face_retention_minutes: 60, face_match_threshold: 0.55, detection_mode: 'face', camera_type: 'client', detection_confidence: 0.35, detection_input_size: 608, tracking_spatial_weight: 0.4, tracking_snap_threshold: 1.5 },
    { id: CAMERA_IDS[3], organization_id: ORG_ID, branch_id: BRANCH_IDS[1], name: 'Escalator Area', location: 'Central atrium', is_active: true, is_online: true, last_seen_at: ago(8000), created_at: ago(50 * 86400000), face_retention_minutes: 30, face_match_threshold: 0.6, detection_mode: 'both', camera_type: 'client', detection_confidence: 0.35, detection_input_size: 608, tracking_spatial_weight: 0.4, tracking_snap_threshold: 1.5 },
    { id: CAMERA_IDS[4], organization_id: ORG_ID, branch_id: BRANCH_IDS[2], name: 'Checkout Area', location: 'Ground floor', is_active: true, is_online: true, last_seen_at: ago(1000), created_at: ago(40 * 86400000), face_retention_minutes: 45, face_match_threshold: 0.65, detection_mode: 'both', camera_type: 'client', detection_confidence: 0.35, detection_input_size: 608, tracking_spatial_weight: 0.4, tracking_snap_threshold: 1.5 },
    { id: CAMERA_IDS[5], organization_id: ORG_ID, branch_id: BRANCH_IDS[2], name: 'Parking Entrance', location: 'Basement P1', is_active: true, is_online: true, last_seen_at: ago(6000), created_at: ago(35 * 86400000), face_retention_minutes: 30, face_match_threshold: 0.6, detection_mode: 'zone', camera_type: 'client', detection_confidence: 0.35, detection_input_size: 608, tracking_spatial_weight: 0.4, tracking_snap_threshold: 1.5 },
    { id: CAMERA_IDS[6], organization_id: ORG_ID, branch_id: BRANCH_IDS[0], name: 'Security Desk', location: 'Ground floor reception', is_active: true, is_online: true, last_seen_at: ago(4000), created_at: ago(70 * 86400000), face_retention_minutes: 60, face_match_threshold: 0.55, detection_mode: 'face', camera_type: 'client', detection_confidence: 0.35, detection_input_size: 608, tracking_spatial_weight: 0.4, tracking_snap_threshold: 1.5 },
    { id: CAMERA_IDS[7], organization_id: ORG_ID, branch_id: BRANCH_IDS[1], name: 'Exit Gate', location: 'Main exit, ground floor', is_active: true, is_online: true, last_seen_at: ago(1500), created_at: ago(48 * 86400000), face_retention_minutes: 30, face_match_threshold: 0.6, detection_mode: 'face', camera_type: 'client', detection_confidence: 0.35, detection_input_size: 608, tracking_spatial_weight: 0.4, tracking_snap_threshold: 1.5 },
    { id: CAMERA_IDS[8], organization_id: ORG_ID, branch_id: BRANCH_IDS[2], name: 'Staff Entrance', location: 'Rear access, Level B1', is_active: true, is_online: true, last_seen_at: ago(7000), created_at: ago(38 * 86400000), face_retention_minutes: 120, face_match_threshold: 0.5, detection_mode: 'face', camera_type: 'client', detection_confidence: 0.35, detection_input_size: 608, tracking_spatial_weight: 0.4, tracking_snap_threshold: 1.5 },
    { id: CAMERA_IDS[9], organization_id: ORG_ID, branch_id: BRANCH_IDS[0], name: 'Parking Lot RTSP', location: 'Outdoor parking area', is_active: true, is_online: true, last_seen_at: ago(3000), created_at: ago(10 * 86400000), face_retention_minutes: 30, face_match_threshold: 0.6, detection_mode: 'both', camera_type: 'rtsp', rtsp_url: 'rtsp://192.168.1.50:554/stream1', detection_confidence: 0.35, detection_input_size: 608, tracking_spatial_weight: 0.4, tracking_snap_threshold: 1.5 },
  ];

  const organization: Organization = {
    id: ORG_ID, name: 'Demo Organization', owner_id: 'demo-user',
    face_recognition_enabled: true, zone_monitoring_enabled: true,
    timezone: 'Africa/Johannesburg',
    is_suspended: false,
    created_at: ago(100 * 86400000), updated_at: ago(5 * 86400000),
  };

  const faces: Face[] = [
    { id: FACE_IDS[0], client_face_id: 'cf-001', label: 'Sarah Johnson', sample_count: 12, first_seen: ago(30 * 86400000), last_seen: ago(3600000), thumbnail: svgThumbnail('SJ', 340), is_active: true, created_at: ago(30 * 86400000), updated_at: ago(3600000) },
    { id: FACE_IDS[1], client_face_id: 'cf-002', label: 'Mike Chen', sample_count: 8, first_seen: ago(20 * 86400000), last_seen: ago(7200000), thumbnail: svgThumbnail('MC', 210), is_active: true, created_at: ago(20 * 86400000), updated_at: ago(7200000) },
    { id: FACE_IDS[2], client_face_id: 'cf-003', label: 'Thandi Nkosi', sample_count: 15, first_seen: ago(45 * 86400000), last_seen: ago(1800000), thumbnail: svgThumbnail('TN', 30), is_active: true, created_at: ago(45 * 86400000), updated_at: ago(1800000) },
    { id: FACE_IDS[3], client_face_id: 'cf-004', label: null, sample_count: 3, first_seen: ago(5 * 86400000), last_seen: ago(86400000), thumbnail: svgThumbnail('??', 120), is_active: true, created_at: ago(5 * 86400000), updated_at: ago(86400000) },
  ];

  const zones: Zone[] = [
    { id: ZONE_IDS[0], camera_id: CAMERA_IDS[0], name: 'Entry Counter', x: 0.1, y: 0.2, width: 0.3, height: 0.4, low_stock_threshold: null, is_active: true, created_at: ago(60 * 86400000), updated_at: ago(86400000), measurement_type: 'person_dwell', product_category: null, dwell_threshold_seconds: 5, product_recognition_enabled: false },
    { id: ZONE_IDS[1], camera_id: CAMERA_IDS[1], name: 'TV Display Wall', x: 0.05, y: 0.1, width: 0.5, height: 0.6, low_stock_threshold: 3, is_active: true, created_at: ago(50 * 86400000), updated_at: ago(86400000), measurement_type: 'item_count', product_category: 'Electronics', dwell_threshold_seconds: null, product_recognition_enabled: true },
    { id: ZONE_IDS[2], camera_id: CAMERA_IDS[2], name: 'Seating Area', x: 0.2, y: 0.3, width: 0.6, height: 0.5, low_stock_threshold: null, is_active: true, created_at: ago(40 * 86400000), updated_at: ago(86400000), measurement_type: 'person_dwell', product_category: null, dwell_threshold_seconds: 10, product_recognition_enabled: false },
    { id: ZONE_IDS[3], camera_id: CAMERA_IDS[3], name: 'Promo Stand', x: 0.3, y: 0.1, width: 0.4, height: 0.5, low_stock_threshold: 5, is_active: true, created_at: ago(35 * 86400000), updated_at: ago(86400000), measurement_type: 'item_count', product_category: 'Promotions', dwell_threshold_seconds: null, product_recognition_enabled: true },
    { id: ZONE_IDS[4], camera_id: CAMERA_IDS[4], name: 'Checkout Queue', x: 0.1, y: 0.4, width: 0.8, height: 0.3, low_stock_threshold: null, is_active: true, created_at: ago(30 * 86400000), updated_at: ago(86400000), measurement_type: 'person_dwell', product_category: null, dwell_threshold_seconds: 15, product_recognition_enabled: false },
    { id: ZONE_IDS[5], camera_id: CAMERA_IDS[5], name: 'Parking Bay A', x: 0.0, y: 0.0, width: 0.5, height: 1.0, low_stock_threshold: null, is_active: true, created_at: ago(25 * 86400000), updated_at: ago(86400000), measurement_type: 'person_dwell', product_category: null, dwell_threshold_seconds: 3, product_recognition_enabled: false },
  ];

  const videos: Video[] = [
    { id: VIDEO_IDS[0], organization_id: ORG_ID, filename: 'summer-sale-promo.mp4', storage_path: '/videos/summer-sale.mp4', content_type: 'video/mp4', duration_seconds: 30, size_bytes: 15000000, target_gender: 'female', target_age_min: 18, target_age_max: 34, created_at: ago(14 * 86400000) },
    { id: VIDEO_IDS[1], organization_id: ORG_ID, filename: 'new-arrivals-2026.mp4', storage_path: '/videos/new-arrivals.mp4', content_type: 'video/mp4', duration_seconds: 45, size_bytes: 22000000, target_gender: null, target_age_min: 25, target_age_max: 54, created_at: ago(7 * 86400000) },
    { id: VIDEO_IDS[2], organization_id: ORG_ID, filename: 'store-welcome.mp4', storage_path: '/videos/welcome.mp4', content_type: 'video/mp4', duration_seconds: 15, size_bytes: 8000000, target_gender: null, target_age_min: null, target_age_max: null, created_at: ago(30 * 86400000) },
  ];

  const mediaPlayers: MediaPlayer[] = [
    { id: PLAYER_IDS[0], organization_id: ORG_ID, branch_id: BRANCH_IDS[0], camera_id: CAMERA_IDS[0], name: 'Entrance Screen', registration_code: null, registration_code_expires_at: null, is_active: true, is_online: true, last_seen_at: ago(10000), demographic_cooldown_seconds: 30, created_at: ago(60 * 86400000) },
    { id: PLAYER_IDS[1], organization_id: ORG_ID, branch_id: BRANCH_IDS[2], camera_id: CAMERA_IDS[4], name: 'Checkout Display', registration_code: null, registration_code_expires_at: null, is_active: true, is_online: true, last_seen_at: ago(15000), demographic_cooldown_seconds: 45, created_at: ago(30 * 86400000) },
  ];

  const playlists: Record<string, PlaylistItem[]> = {
    [PLAYER_IDS[0]]: [
      { id: uuid(), video_id: VIDEO_IDS[2], item_type: 'video', url: null, duration_seconds: 15, position: 0, video: videos[2] },
      { id: uuid(), video_id: VIDEO_IDS[0], item_type: 'video', url: null, duration_seconds: 30, position: 1, video: videos[0] },
    ],
    [PLAYER_IDS[1]]: [
      { id: uuid(), video_id: VIDEO_IDS[1], item_type: 'video', url: null, duration_seconds: 45, position: 0, video: videos[1] },
    ],
  };

  const products: Product[] = [
    { id: uuid(), client_product_id: 'prod-001', name: 'Coca-Cola 500ml', category: 'Beverages', sku: 'CC-500', descriptor: '', sample_count: 24, thumbnails: [svgThumbnail('Cola', 0)], is_unknown: false, is_active: true, created_at: ago(20 * 86400000), updated_at: ago(86400000) },
    { id: uuid(), client_product_id: 'prod-002', name: 'Lays Classic Chips', category: 'Snacks', sku: 'LC-200', descriptor: '', sample_count: 18, thumbnails: [svgThumbnail('Lays', 45)], is_unknown: false, is_active: true, created_at: ago(15 * 86400000), updated_at: ago(86400000) },
    { id: uuid(), client_product_id: 'prod-003', name: 'Samsung Galaxy S26', category: 'Electronics', sku: 'SG-S26', descriptor: '', sample_count: 6, thumbnails: [svgThumbnail('S26', 220)], is_unknown: false, is_active: true, created_at: ago(10 * 86400000), updated_at: ago(86400000) },
    { id: uuid(), client_product_id: 'prod-004', name: 'Unknown Item #1', category: null, sku: null, descriptor: '', sample_count: 2, thumbnails: [svgThumbnail('???', 0)], is_unknown: true, is_active: true, created_at: ago(2 * 86400000), updated_at: ago(86400000) },
  ];

  const users: User[] = [
    { id: 'demo-user', email: 'demo@dominate.io', role: 'admin', organization_id: ORG_ID, created_at: ago(100 * 86400000) },
    { id: 'user-2', email: 'analyst@dominate.io', role: 'user', organization_id: ORG_ID, created_at: ago(60 * 86400000) },
  ];

  return { branches, cameras, organization, faces, zones, videos, mediaPlayers, playlists, products, users };
}

// ─── Mutable Store ────────────────────────────────────────────
let store = createBaseData();
let liveFeedCounter = 1000;

export function resetDemoData() {
  store = createBaseData();
  liveFeedCounter = 1000;
}

// ─── Period Multiplier ────────────────────────────────────────
function periodMultiplier(period: string): number {
  const map: Record<string, number> = { '1h': 0.04, '6h': 0.25, '12h': 0.5, 'today': 1, '7d': 7, '30d': 30 };
  return map[period] || 7;
}

function periodHours(period: string): number {
  const map: Record<string, number> = { '1h': 1, '6h': 6, '12h': 12, 'today': 24, '7d': 168, '30d': 720 };
  return map[period] || 168;
}

// ─── Analytics Generators ─────────────────────────────────────
function generateSummary(period: string): AnalyticsSummary {
  const m = periodMultiplier(period);
  const total = Math.round(340 * m + rand(-20, 20) * m);
  const unique = Math.round(total * 0.72);
  const male = Math.round(total * 0.53);
  return {
    total_detections: total,
    unique_viewers: unique,
    avg_attention_ms: rand(4500, 8500),
    gender_distribution: { male, female: total - male },
    age_distribution: {
      '0-10': Math.round(total * 0.03), '11-17': Math.round(total * 0.08),
      '18-24': Math.round(total * 0.22), '25-34': Math.round(total * 0.28),
      '35-44': Math.round(total * 0.19), '45-54': Math.round(total * 0.11),
      '55-64': Math.round(total * 0.06), '65+': Math.round(total * 0.03),
    },
    expression_distribution: {
      neutral: Math.round(total * 0.35), happy: Math.round(total * 0.28),
      sad: Math.round(total * 0.05), angry: Math.round(total * 0.03),
      fearful: Math.round(total * 0.02), disgusted: Math.round(total * 0.02),
      surprised: Math.round(total * 0.08),
    },
    peak_hour: 13,
  };
}

function generateTimeseries(period: string, interval: number): TimeseriesPoint[] {
  const hours = periodHours(period);
  const stepMs = interval * 60 * 1000;
  const steps = Math.min(Math.floor((hours * 3600000) / stepMs), 200);
  const points: TimeseriesPoint[] = [];
  const startMs = Date.now() - hours * 3600000;

  for (let i = 0; i < steps; i++) {
    const t = startMs + i * stepMs;
    const hour = new Date(t).getHours();
    // Bell curve peaking at lunch/afternoon
    const base = Math.max(2, 30 * Math.exp(-0.5 * ((hour - 13) / 4) ** 2));
    points.push({
      timestamp: new Date(t).toISOString(),
      count: Math.round(base + rand(-5, 8)),
    });
  }
  return points;
}

function generateEngagement(): EngagementMetrics {
  const total = rand(800, 1200);
  const glancers = Math.round(total * 0.45);
  const browsers = Math.round(total * 0.35);
  const engaged = total - glancers - browsers;
  return {
    dwell_distribution: {
      min_ms: 500, max_ms: 45000, median_ms: 4200,
      p25_ms: 1800, p75_ms: 9500, p90_ms: 18000, avg_ms: 6300,
    },
    engagement_tiers: {
      glancers: { count: glancers, percentage: +(glancers / total * 100).toFixed(1) },
      browsers: { count: browsers, percentage: +(browsers / total * 100).toFixed(1) },
      engaged: { count: engaged, percentage: +(engaged / total * 100).toFixed(1) },
    },
    dwell_histogram: [
      { range_label: '0-2s', count: Math.round(total * 0.25) },
      { range_label: '2-5s', count: Math.round(total * 0.22) },
      { range_label: '5-10s', count: Math.round(total * 0.20) },
      { range_label: '10-20s', count: Math.round(total * 0.15) },
      { range_label: '20-30s', count: Math.round(total * 0.10) },
      { range_label: '30-60s', count: Math.round(total * 0.05) },
      { range_label: '60s+', count: Math.round(total * 0.03) },
    ],
  };
}

function generateTrafficPatterns(): TrafficPatterns {
  return {
    hourly_breakdown: Array.from({ length: 24 }, (_, h) => {
      const base = Math.max(5, 120 * Math.exp(-0.5 * ((h - 13) / 4) ** 2));
      return { hour: h, count: Math.round(base + rand(-10, 15)), avg_attention_ms: rand(3000, 9000) };
    }),
    day_of_week: [
      { day: 0, day_name: 'Sunday', count: rand(180, 260), avg_attention_ms: rand(5000, 8000) },
      { day: 1, day_name: 'Monday', count: rand(300, 400), avg_attention_ms: rand(4000, 7000) },
      { day: 2, day_name: 'Tuesday', count: rand(320, 420), avg_attention_ms: rand(4500, 7500) },
      { day: 3, day_name: 'Wednesday', count: rand(310, 410), avg_attention_ms: rand(4500, 7000) },
      { day: 4, day_name: 'Thursday', count: rand(340, 440), avg_attention_ms: rand(4000, 7000) },
      { day: 5, day_name: 'Friday', count: rand(380, 500), avg_attention_ms: rand(5000, 8000) },
      { day: 6, day_name: 'Saturday', count: rand(420, 550), avg_attention_ms: rand(6000, 9000) },
    ],
  };
}

function generateCrossDemographics(): CrossDemographicInsights {
  return {
    expression_by_gender: EXPRESSIONS.map(e => ({
      expression: e, male_count: rand(10, 80), female_count: rand(10, 80),
    })),
    age_engagement: AGE_GROUPS.map(g => ({
      age_group: g, avg_attention_ms: rand(3000, 12000), count: rand(20, 150),
    })),
    expression_attention: EXPRESSIONS.map(e => ({
      expression: e, avg_attention_ms: rand(2000, 15000), count: rand(15, 100),
    })),
  };
}

function generateVideoStats(): VideoStatsEntry[] {
  return store.videos.map(v => ({
    video_id: v.id, filename: v.filename,
    target_gender: v.target_gender, target_age_min: v.target_age_min, target_age_max: v.target_age_max,
    total_plays: rand(50, 500), unique_viewers: rand(30, 300),
  }));
}

function generateLiveFeed(limit: number): LiveFeedEvent[] {
  const count = rand(3, 8);
  const events: LiveFeedEvent[] = [];
  for (let i = 0; i < Math.min(count, limit); i++) {
    const cam = pick(store.cameras);
    const useFace = Math.random() < 0.2;
    events.push({
      id: liveFeedCounter++,
      camera_id: cam.id,
      camera_name: cam.name,
      timestamp: new Date(Date.now() - i * rand(500, 3000)).toISOString(),
      age_group: pick([...AGE_GROUPS]),
      gender: pick([...GENDERS]),
      gender_confidence: +(0.7 + Math.random() * 0.29).toFixed(2),
      expression: pick([...EXPRESSIONS]),
      expression_confidence: +(0.5 + Math.random() * 0.49).toFixed(2),
      attention_duration_ms: rand(500, 25000),
      face_id: useFace ? pick(FACE_IDS) : null,
    });
  }
  return events;
}

function generateLiveStats(): LiveCameraStats {
  return {
    detection_count: rand(5, 30),
    male_count: rand(2, 15),
    female_count: rand(2, 15),
    age_distribution: Object.fromEntries(AGE_GROUPS.map(g => [g, rand(0, 8)])),
    expression_distribution: Object.fromEntries(EXPRESSIONS.map(e => [e, rand(0, 10)])),
    last_detection: ago(rand(1000, 10000)),
  };
}

function generateSnapshotSvg(cameraName: string): Blob {
  const hue = (cameraName.charCodeAt(0) * 37) % 360;
  const time = new Date().toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', second: '2-digit' });
  const people = rand(0, 12);
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="640" height="360">
    <defs>
      <linearGradient id="g" x1="0%" y1="0%" x2="100%" y2="100%">
        <stop offset="0%" style="stop-color:hsl(${hue},40%,20%)"/>
        <stop offset="100%" style="stop-color:hsl(${(hue + 40) % 360},40%,30%)"/>
      </linearGradient>
    </defs>
    <rect width="640" height="360" fill="url(#g)"/>
    <circle cx="600" cy="26" r="8" fill="#ef4444" opacity="0.9"/>
    <text x="582" y="31" fill="white" font-size="12" font-family="sans-serif" text-anchor="end" font-weight="bold">LIVE</text>
    <text x="320" y="180" text-anchor="middle" fill="white" font-size="22" font-family="sans-serif" opacity="0.8">${cameraName}</text>
    <text x="320" y="210" text-anchor="middle" fill="white" font-size="13" font-family="sans-serif" opacity="0.4">Demo Feed</text>
    <rect x="20" y="320" width="140" height="28" rx="4" fill="rgba(0,0,0,0.5)"/>
    <text x="30" y="340" fill="white" font-size="12" font-family="monospace" opacity="0.9">${time}</text>
    <rect x="490" y="320" width="130" height="28" rx="4" fill="rgba(0,0,0,0.5)"/>
    <text x="500" y="340" fill="white" font-size="12" font-family="sans-serif" opacity="0.9">${people} people in view</text>
  </svg>`;
  return new Blob([svg], { type: 'image/svg+xml' });
}

// ─── Exported Demo Data API ───────────────────────────────────
export const demoData = {
  // Organization
  organizations: {
    list: (): Organization[] => [store.organization],
    get: (): Organization => store.organization,
    create: (name: string): Organization => {
      store.organization = { ...store.organization, name };
      return store.organization;
    },
    update: (_id: string, name: string): Organization => {
      store.organization.name = name;
      return store.organization;
    },
    updateSettings: (_id: string, settings: Partial<Organization>): Organization => {
      Object.assign(store.organization, settings);
      return store.organization;
    },
  },

  // Branches
  branches: {
    list: (): Branch[] => store.branches,
    get: (id: string): Branch | undefined => store.branches.find(b => b.id === id),
    create: (data: { name: string; address?: string | null }): Branch => {
      const b: Branch = { id: uuid(), organization_id: ORG_ID, name: data.name, address: data.address ?? null, created_at: now(), updated_at: now() };
      store.branches.push(b);
      return b;
    },
    update: (id: string, data: Partial<Branch>): Branch => {
      const b = store.branches.find(x => x.id === id);
      if (b) Object.assign(b, data, { updated_at: now() });
      return b!;
    },
    delete: (id: string) => { store.branches = store.branches.filter(b => b.id !== id); },
  },

  // Cameras
  cameras: {
    list: (): Camera[] => store.cameras,
    get: (id: string): Camera | undefined => store.cameras.find(c => c.id === id),
    register: (name: string, location?: string, branchId?: string): CameraWithKey => {
      const c: CameraWithKey = {
        id: uuid(), organization_id: ORG_ID, branch_id: branchId || null,
        name, location, is_active: true, is_online: false,
        last_seen_at: undefined, created_at: now(),
        face_retention_minutes: 30, face_match_threshold: 0.6, detection_mode: 'both',
        camera_type: 'client',
        detection_confidence: 0.35, detection_input_size: 608,
        tracking_spatial_weight: 0.4, tracking_snap_threshold: 1.5,
        api_key: `demo-key-${uuid().slice(0, 8)}`,
      };
      store.cameras.push(c);
      return c;
    },
    update: (id: string, data: Partial<Camera>): Camera => {
      const c = store.cameras.find(x => x.id === id);
      if (c) Object.assign(c, data);
      return c!;
    },
    delete: (id: string) => { store.cameras = store.cameras.filter(c => c.id !== id); },
    move: (id: string): Camera => {
      const c = store.cameras.find(x => x.id === id);
      return c!;
    },
    regenerateApiKey: (id: string): CameraWithKey => {
      const c = store.cameras.find(x => x.id === id);
      return { ...c!, api_key: `demo-key-${uuid().slice(0, 8)}` };
    },
    getLiveStats: (): LiveCameraStats => generateLiveStats(),
    updateSettings: (id: string, settings: Record<string, unknown>): Camera => {
      const c = store.cameras.find(x => x.id === id);
      if (c) Object.assign(c, settings);
      return c!;
    },
    getSettingsHistory: (id: string): CameraSettingsHistory[] => [
      { id: 1, camera_id: id, setting_name: 'detection_mode', old_value: 'face', new_value: 'both', changed_at: ago(7 * 86400000) },
      { id: 2, camera_id: id, setting_name: 'face_match_threshold', old_value: '0.5', new_value: '0.6', changed_at: ago(3 * 86400000) },
    ],
    getSnapshot: (id: string): Blob => {
      const cam = store.cameras.find(c => c.id === id);
      return generateSnapshotSvg(cam?.name || 'Camera');
    },
    getSnapshotStatus: (): SnapshotStatus => ({
      available: true, requested: false, requested_at: null, snapshot_at: ago(60000),
    }),
  },

  // Analytics
  analytics: {
    getSummary: (period: string): AnalyticsSummary => generateSummary(period),
    getTimeseries: (period: string, interval: number): TimeseriesPoint[] => generateTimeseries(period, interval),
    getEngagement: (): EngagementMetrics => generateEngagement(),
    getTrafficPatterns: (): TrafficPatterns => generateTrafficPatterns(),
    getCrossDemographics: (): CrossDemographicInsights => generateCrossDemographics(),
    getVideoStats: (): VideoStatsEntry[] => generateVideoStats(),
    getLiveFeed: (limit: number): LiveFeedEvent[] => generateLiveFeed(limit),
    clearData: (): { deleted: number } => ({ deleted: 0 }),
  },

  // Faces
  faces: {
    list: (): Face[] => store.faces,
    get: (id: string): Face | undefined => store.faces.find(f => f.id === id),
    update: (id: string, data: { label?: string | null }): Face => {
      const f = store.faces.find(x => x.id === id);
      if (f && data.label !== undefined) f.label = data.label;
      return f!;
    },
    delete: (id: string) => { store.faces = store.faces.filter(f => f.id !== id); },
  },

  // Zones
  zones: {
    list: (): Zone[] => store.zones,
    listForCamera: (cameraId: string): Zone[] => store.zones.filter(z => z.camera_id === cameraId),
    get: (id: string): Zone | undefined => store.zones.find(z => z.id === id),
    create: (cameraId: string, data: Record<string, unknown>): Zone => {
      const z: Zone = {
        id: uuid(), camera_id: cameraId,
        name: (data.name as string) || 'New Zone',
        x: (data.x as number) || 0, y: (data.y as number) || 0,
        width: (data.width as number) || 0.3, height: (data.height as number) || 0.3,
        low_stock_threshold: (data.low_stock_threshold as number) ?? null,
        is_active: true, created_at: now(), updated_at: now(),
        measurement_type: (data.measurement_type as Zone['measurement_type']) || 'item_count',
        product_category: (data.product_category as string) ?? null,
        dwell_threshold_seconds: (data.dwell_threshold_seconds as number) ?? null,
        product_recognition_enabled: false,
      };
      store.zones.push(z);
      return z;
    },
    update: (id: string, data: Record<string, unknown>): Zone => {
      const z = store.zones.find(x => x.id === id);
      if (z) Object.assign(z, data, { updated_at: now() });
      return z!;
    },
    delete: (id: string) => { store.zones = store.zones.filter(z => z.id !== id); },
    getCountHistory: (): ZoneCountEvent[] => {
      const events: ZoneCountEvent[] = [];
      for (let i = 0; i < 20; i++) {
        events.push({
          id: i + 1, zone_id: ZONE_IDS[0], camera_id: CAMERA_IDS[0],
          timestamp: ago((20 - i) * 300000), count: rand(3, 20),
          detected_objects: null, created_at: ago((20 - i) * 300000),
        });
      }
      return events;
    },
    getAnalyticsSummary: (): ZoneAnalyticsSummary => {
      const zwc = store.zones.map(z => {
        const cam = store.cameras.find(c => c.id === z.camera_id);
        return {
          zone: z,
          current_count: rand(2, 25),
          camera_name: cam?.name || null,
        };
      });
      const lowStock = zwc.filter(w => w.zone.low_stock_threshold !== null && w.current_count !== null && w.current_count < w.zone.low_stock_threshold).length;
      return {
        total_zones: store.zones.length,
        total_items: zwc.reduce((s, w) => s + (w.current_count || 0), 0),
        low_stock_alerts: lowStock,
        zones_with_counts: zwc,
      };
    },
    getLatestCounts: (cameraId: string): LatestZoneCount[] => {
      return store.zones.filter(z => z.camera_id === cameraId).map(z => ({
        zone_id: z.id, zone_name: z.name, count: rand(2, 20), timestamp: ago(rand(5000, 30000)),
      }));
    },
  },

  // Products
  products: {
    list: (params?: { unknown_only?: boolean }): Product[] => {
      if (params?.unknown_only) return store.products.filter(p => p.is_unknown);
      return store.products;
    },
    get: (id: string): Product | undefined => store.products.find(p => p.id === id),
    create: (data: Record<string, unknown>): Product => {
      const p: Product = {
        id: uuid(), client_product_id: `prod-${uuid().slice(0, 6)}`,
        name: (data.name as string) || 'New Product',
        category: (data.category as string) ?? null, sku: (data.sku as string) ?? null,
        descriptor: (data.descriptor as string) || '', sample_count: 1,
        thumbnails: (data.thumbnails as string[]) || [], is_unknown: false, is_active: true,
        created_at: now(), updated_at: now(),
      };
      store.products.push(p);
      return p;
    },
    update: (id: string, data: Record<string, unknown>): Product => {
      const p = store.products.find(x => x.id === id);
      if (p) Object.assign(p, data, { updated_at: now() });
      return p!;
    },
    delete: (id: string) => { store.products = store.products.filter(p => p.id !== id); },
    clearAll: (): { deleted: number } => { const n = store.products.length; store.products = []; return { deleted: n }; },
    getZoneSightings: (): never[] => [],
    updateZoneProductRecognition: () => {},
  },

  // Videos
  videos: {
    list: (): Video[] => store.videos,
    get: (id: string): Video | undefined => store.videos.find(v => v.id === id),
    upload: (filename: string): Video => {
      const v: Video = {
        id: uuid(), organization_id: ORG_ID, filename,
        storage_path: `/videos/${filename}`, content_type: 'video/mp4',
        duration_seconds: rand(10, 120), size_bytes: rand(5000000, 50000000),
        target_gender: null, target_age_min: null, target_age_max: null, created_at: now(),
      };
      store.videos.push(v);
      return v;
    },
    delete: (id: string) => { store.videos = store.videos.filter(v => v.id !== id); },
    updateDemographics: (id: string, data: Record<string, unknown>): Video => {
      const v = store.videos.find(x => x.id === id);
      if (v) Object.assign(v, data);
      return v!;
    },
  },

  // Media Players
  mediaPlayers: {
    list: (): MediaPlayer[] => store.mediaPlayers,
    get: (id: string): MediaPlayer | undefined => store.mediaPlayers.find(p => p.id === id),
    create: (name: string, cameraId?: string, branchId?: string): MediaPlayerWithKey => {
      const p: MediaPlayerWithKey = {
        id: uuid(), organization_id: ORG_ID, branch_id: branchId || null,
        camera_id: cameraId || null, name,
        registration_code: `DEMO-${rand(1000, 9999)}`,
        registration_code_expires_at: new Date(Date.now() + 3600000).toISOString(),
        is_active: true, is_online: false, last_seen_at: null,
        demographic_cooldown_seconds: 30, created_at: now(),
        api_key: `demo-mp-key-${uuid().slice(0, 8)}`,
      };
      store.mediaPlayers.push(p);
      return p;
    },
    update: (id: string, data: Partial<MediaPlayer>): MediaPlayer => {
      const p = store.mediaPlayers.find(x => x.id === id);
      if (p) Object.assign(p, data);
      return p!;
    },
    delete: (id: string) => { store.mediaPlayers = store.mediaPlayers.filter(p => p.id !== id); },
    regenerateCode: (): { registration_code: string } => ({ registration_code: `DEMO-${rand(1000, 9999)}` }),
    getPlaylist: (id: string): PlaylistItem[] => store.playlists[id] || [],
    updatePlaylist: (id: string, items: PlaylistItem[]): PlaylistItem[] => {
      store.playlists[id] = items;
      return items;
    },
  },

  // Users
  users: {
    list: (): User[] => store.users,
    get: (id: string): User | undefined => store.users.find(u => u.id === id),
    update: (id: string, data: Partial<User>): User => {
      const u = store.users.find(x => x.id === id);
      if (u) Object.assign(u, data);
      return u!;
    },
    delete: (id: string) => { store.users = store.users.filter(u => u.id !== id); },
  },

  // Alerts
  alerts: {
    list: (): Alert[] => [
      { id: 1, organization_id: ORG_ID, zone_id: ZONE_IDS[1], camera_id: CAMERA_IDS[1], alert_type: 'low_stock', message: 'TV Display Wall stock below threshold (2 items)', is_read: false, created_at: ago(1800000) },
      { id: 2, organization_id: ORG_ID, zone_id: ZONE_IDS[3], camera_id: CAMERA_IDS[3], alert_type: 'low_stock', message: 'Promo Stand running low (4 items)', is_read: true, created_at: ago(7200000) },
    ],
    markRead: () => {},
    markAllRead: (): { marked: number } => ({ marked: 2 }),
  },

  // Zone Video Mappings
  zoneVideo: {
    getMapping: (zoneId: string): ZoneVideoMapping => ({ zone_id: zoneId, video_id: VIDEO_IDS[0], video: store.videos[0] }),
    setMapping: (zoneId: string, videoId: string): ZoneVideoMapping => ({ zone_id: zoneId, video_id: videoId, video: store.videos.find(v => v.id === videoId) }),
    removeMapping: () => {},
  },
};
